import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { auth } from '../../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { setLocation } from '../../store/slices/authSlice';

const ProtectedAuthRoute = ({ children }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [currentUser, setCurrentUser] = useState(false);
    const [loading, setLoading] = useState(true);
    const authState = useSelector(state => state.auth);
    const locationState = useSelector(state => state.location);

    const hasAuthLocation = authState.location?.coordinates?.length > 0;
    const hasSliceLocation = locationState.location?.coordinates?.length > 0;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (authState.uid && !hasAuthLocation && hasSliceLocation) {
            dispatch(setLocation({
                type: 'Point',
                coordinates: locationState.location.coordinates,
                geoHash: locationState.location.geoHash || ''
            }));
        }
    }, [authState.uid, dispatch, hasAuthLocation, hasSliceLocation, locationState.location]);

    if (loading) {
        return null;
    }

    const isProfileComplete = () => {
        return authState.name && 
               authState.doB && 
               authState.gender && 
               authState.genderSearch && 
               authState.LookingFor && 
               authState.acceptedPolicy && 
               authState.photos?.length >= 2 &&
               (hasAuthLocation || hasSliceLocation);
    };

    const getRedirectPath = () => {
        const pathname = location.pathname;
     

        // Si l'utilisateur est connecté et son profil est complet, rediriger vers home depuis les pages d'auth
        if (currentUser && authState.uid && isProfileComplete() && 
            (pathname === '/' || pathname === '/login' || pathname === '/signup' || pathname.startsWith('/auth/'))) {
            return '/home';
        }

        // Pages publiques qui ne nécessitent pas d'authentification
        const publicPages = ['/auth/phonelogin', '/auth/otp','/','/login','/signup'];
        if (publicPages.includes(pathname)) return null;


        // Si pas authentifié du tout, rediriger vers welcome
        if (!currentUser || !authState.uid) {
            return '/';
        }

        // Étapes du parcours dans l'ordre logique, chacune avec sa condition de complétion.
        // Sert à autoriser le retour sur une étape déjà atteinte (pour modification),
        // tout en bloquant le saut par-dessus une étape non remplie.
        const steps = [
            { path: '/auth/welcome-policy', isComplete: () => !!authState.acceptedPolicy },
            { path: '/auth/first-name', isComplete: () => !!authState.name && !!authState.doB },
            { path: '/auth/your-gender', isComplete: () => !!authState.gender },
            { path: '/auth/interested', isComplete: () => !!authState.genderSearch },
            { path: '/auth/looking-for', isComplete: () => !!authState.LookingFor },
            { path: '/auth/recent-pics', isComplete: () => (authState.photos?.length || 0) >= 2 },
            { path: '/auth/location', isComplete: () => hasAuthLocation || hasSliceLocation }
        ];

        const currentStepIndex = steps.findIndex(step => step.path === pathname);
        if (currentStepIndex === -1) return null;

        // Première étape non complétée : la limite au-delà de laquelle on ne peut pas avancer
        const firstIncompleteIndex = steps.findIndex(step => !step.isComplete());
        const wallIndex = firstIncompleteIndex === -1 ? steps.length : firstIncompleteIndex;

        // Autorisé : étape déjà atteinte (navigation arrière libre pour modifier) ou étape courante à remplir
        if (currentStepIndex <= wallIndex) return null;

        // Tentative de sauter une étape non remplie : renvoyer vers cette étape
        return steps[wallIndex]?.path ?? null;
    };

    const redirectPath = getRedirectPath();
    if (redirectPath) {
        return <Navigate to={redirectPath} replace />;
    }

    return children;
};

export default ProtectedAuthRoute;
